import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, BarChart3, Bell, MapPin, Sparkles
} from 'lucide-react';

const TABS = [
  { to: '/',          label: 'Home',      icon: LayoutDashboard },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/alerts',    label: 'Alerts',    icon: Bell },
  { to: '/gps',       label: 'GPS',       icon: MapPin },
  { to: '/ai',        label: 'AI',        icon: Sparkles }
];

export default function BottomNav({ visible }) {
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {visible && (
        <motion.nav
          key="bottom-nav"
          initial={{ y: 96, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 96, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
          className="fixed left-1/2 -translate-x-1/2 bottom-safe z-50 w-[min(96vw,520px)]"
        >
          <div className="card-surface rounded-3xl px-2 py-2 flex items-center justify-between gap-1">
            {TABS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className="flex-1"
                onClick={() => {
                  if (navigator.vibrate) navigator.vibrate(8);
                }}
              >
                {({ isActive }) => (
                  <motion.div
                    whileTap={{ scale: 0.9 }}
                    className="relative flex flex-col items-center justify-center py-2 px-1 rounded-2xl"
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute inset-0 rounded-2xl bg-gradient-to-br from-indigo-500/15 to-fuchsia-500/15 ring-1 ring-indigo-500/30"
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                      />
                    )}
                    <Icon
                      className={`w-5 h-5 relative ${
                        isActive ? 'text-indigo-600 dark:text-indigo-400' : 'opacity-70'
                      }`}
                    />
                    <span
                      className={`relative mt-0.5 text-[10px] font-semibold tracking-wide ${
                        isActive ? 'text-indigo-600 dark:text-indigo-400' : 'opacity-70'
                      }`}
                    >
                      {label}
                    </span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-glow"
                        className="absolute -bottom-1 w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.8)]"
                      />
                    )}
                  </motion.div>
                )}
              </NavLink>
            ))}
          </div>

          {/* Floating action button (live page shortcut) */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/live')}
            aria-label="Live monitoring"
            className="absolute -top-7 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full grid place-items-center
              bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white shadow-glow ring-4 ring-white/70 dark:ring-slate-900/70"
          >
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 animate-pulse-soft opacity-50" />
            <Sparkles className="w-6 h-6 relative" />
          </motion.button>
        </motion.nav>
      )}
    </AnimatePresence>
  );
}
